import {
  BlendFunction,
  BloomEffect,
  ChromaticAberrationEffect,
  EffectComposer,
  EffectPass,
  HueSaturationEffect,
  NoiseEffect,
  RenderPass,
  SMAAEffect,
  SMAAPreset,
  ToneMappingEffect,
  ToneMappingMode,
  VignetteEffect,
} from 'postprocessing';
import { HalfFloatType, NoToneMapping, PCFShadowMap, PMREMGenerator, SRGBColorSpace, Vector2, Vector3, WebGLRenderer } from 'three';
import type { PerspectiveCamera, Scene } from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { fieldOfView } from './fieldOfView';
import { ShockRing } from './shockRing';

/** How hard the device can work: phones get fewer pixels and a lighter picture. */
export interface Quality {
  readonly maxPixelRatio: number;
  readonly shadows: boolean;
  readonly shadowMapSize: number;
  readonly crowd: number;
}

export function detectQuality(): Quality {
  const touch = matchMedia('(pointer: coarse)').matches;
  return touch
    ? { maxPixelRatio: 1.5, shadows: true, shadowMapSize: 1024, crowd: 1800 }
    : { maxPixelRatio: 2, shadows: true, shadowMapSize: 2048, crowd: 4200 };
}

/**
 * The renderer and everything done to the finished picture: bloom around the lights, a filmic tone curve, a vignette,
 * a little grain, and the colour runs and draining of being hit, which the effects turn up and down.
 */
export class Pipeline {
  readonly renderer: WebGLRenderer;
  private readonly composer: EffectComposer;
  private readonly aberration: ChromaticAberrationEffect;
  private readonly saturation: HueSaturationEffect;
  private readonly vignette: VignetteEffect;
  private readonly shock: ShockRing;
  // Each of these bends the picture only now and then; the rest of the time its pass would copy the screen for nothing.
  private readonly shockPass: EffectPass;
  private readonly aberrationPass: EffectPass;
  private readonly baseVignette = 0.55;
  private readonly baseSaturation = 0.18;

  constructor(canvas: HTMLCanvasElement, scene: Scene, private readonly camera: PerspectiveCamera, readonly quality: Quality) {
    this.renderer = new WebGLRenderer({ canvas, antialias: false, stencil: false, powerPreference: 'high-performance' });
    this.renderer.outputColorSpace = SRGBColorSpace;
    // The tone curve runs in the effect pass, after bloom has seen the bright lights at full strength.
    this.renderer.toneMapping = NoToneMapping;
    this.renderer.shadowMap.enabled = quality.shadows;
    this.renderer.shadowMap.type = PCFShadowMap;

    const environment = new PMREMGenerator(this.renderer);
    scene.environment = environment.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environmentIntensity = 0.2;
    environment.dispose();

    this.composer = new EffectComposer(this.renderer, { frameBufferType: HalfFloatType });
    this.composer.addPass(new RenderPass(scene, camera));
    const bloom = new BloomEffect({ mipmapBlur: true, luminanceThreshold: 1.1, luminanceSmoothing: 0.25, intensity: 0.9, radius: 0.7 });
    this.aberration = new ChromaticAberrationEffect({ offset: new Vector2(0, 0), radialModulation: true, modulationOffset: 0.2 });
    this.saturation = new HueSaturationEffect({ saturation: this.baseSaturation });
    this.vignette = new VignetteEffect({ offset: 0.3, darkness: this.baseVignette });
    // Overlay keeps the blacks black; a screen blend would lift the whole dark hall to grey.
    const grain = new NoiseEffect({ blendFunction: BlendFunction.OVERLAY });
    grain.blendMode.opacity.value = 0.06;
    // ACES keeps the ring's colours punchy; AgX greys a cartoon palette.
    const toneMapping = new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC });
    this.composer.addPass(new EffectPass(camera, bloom, toneMapping, this.saturation, this.vignette, grain));
    // Each of these reads neighbouring pixels, so each needs a pass of its own.
    this.shock = new ShockRing(camera);
    this.shockPass = new EffectPass(camera, this.shock);
    this.aberrationPass = new EffectPass(camera, this.aberration);
    this.aberrationPass.enabled = false;
    this.composer.addPass(this.shockPass);
    this.composer.addPass(this.aberrationPass);
    this.composer.addPass(new EffectPass(camera, new SMAAEffect({ preset: SMAAPreset.HIGH })));
  }

  resize(width: number, height: number): void {
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.quality.maxPixelRatio));
    this.composer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.fov = fieldOfView(this.camera.aspect);
    this.camera.updateProjectionMatrix();
  }

  /** 0 is the calm picture; 1 is the full sting of a heavy hit: colour runs at the edges, the colour drains, the edges darken. */
  setHurt(level: number): void {
    const offset = 0.004 * level;
    this.aberration.offset.set(offset, offset * 0.6);
    this.aberrationPass.enabled = offset > 0;
    this.saturation.saturation = this.baseSaturation - 0.6 * level;
    this.vignette.darkness = this.baseVignette + 0.35 * level;
  }

  /** A ring of air bending outwards from a heavy blow, strength 0..1. */
  shockwave(at: Vector3, strength: number): void {
    this.shock.explode(at, strength);
  }

  render(seconds: number): void {
    this.shockPass.enabled = this.shock.active;
    this.composer.render(seconds);
  }
}
